import { network } from '@tencent/netcheck';
import { Emitter } from 'nanoevents';
import { purify } from '../../promise-utils';
import { ImageEditErrors, ImageValidator } from '../errors';
import { ImageEditEventHandlers, ImageEditEvents } from '../events';
import EditorImage, { EditorImageFactory } from './editor-image';
import PageExitManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/page-exit-manager';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import ImageRecoveryManager from './image-recovery-manager';
import { ImageResource } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-resource';
import ImageQuickLookManager from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import ImageConverter, { ImageFormat } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-converter';
import ImageDetector from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-detector';

interface CreateImagesContext {
    images: EditorImage[];
    resources: ImageResource[];
}

class ImageCreator {
    private imageRecovery?: ImageRecoveryManager;
    private eventBus: Emitter<ImageEditEventHandlers>;

    public constructor(eventBus: Emitter<ImageEditEventHandlers>, imageRecovery?: ImageRecoveryManager) {
        this.eventBus = eventBus;
        this.imageRecovery = imageRecovery;
    }

    public async create(blobs: Blob[]): Promise<CreateImagesContext> {
        const ctx: CreateImagesContext = { resources: [], images: [] };
        const quickLook = ImageQuickLookManager.getInstance();

        if (!blobs) return ctx;

        if (!network.isOnline && !ENV.IS_LOCAL_EDIT) {
            this.eventBus.emit(ImageEditEvents.CreateImageError, [new Error(ImageEditErrors.networkOffline)]);
            return ctx;
        }

        PageExitManager.getInstance().protect();
        this.eventBus.emit(ImageEditEvents.WillCreateImages, blobs);
        try {
            ctx.resources = await quickLook.bulkAddImageBlobs(blobs);

            await this.clearInvalidImages(ctx);
            await this.buildImages(ctx);
        } catch (err) {
            PageExitManager.getInstance().release();
            this.eventBus.emit(ImageEditEvents.DidCreateImages, ctx.images);
            throw err;
        }

        this.imageRecovery?.backupAll(ctx.resources).finally(() => {
            PageExitManager.getInstance().release();
            this.eventBus.emit(ImageEditEvents.DidCreateImages, ctx.images);
        });

        return ctx;
    }

    private async buildImages(ctx: CreateImagesContext) {
        const quickLook = ImageQuickLookManager.getInstance();
        const resources: ImageResource[] = [];

        const requests = ctx.resources.map(async (resource) => {
            const meta = await resource.loadMeta();
            const image = EditorImageFactory.create(resource.id, meta);

            if (ImageDetector.isGif(resource.getBlob())) {
                // 获取 Gif 首帧图
                const firstFrameBlob = await ImageConverter.convertTo(
                    resource.getQuickLookUrl(),
                    ImageFormat.JPEG,
                    { isLimitSize: true },
                );
                const firstFrameResource = await quickLook.addImageBlob(firstFrameBlob);

                // 保存 Gif 首帧图信息到 PictureModel
                await firstFrameResource.loadMeta();
                image.backupSrc = firstFrameResource.id;

                // 首帧图需要上传
                resources.push(firstFrameResource);
            }

            resources.push(resource);
            return image;
        });

        const [images, errors] = await purify(requests);

        if (errors.length > 0) {
            this.eventBus.emit(ImageEditEvents.CreateImageError, errors);
        }

        ctx.resources = resources;
        ctx.images = images;
    }

    private async clearInvalidImages(ctx: CreateImagesContext) {
        const [validResources] = await purify(ctx.resources.map(resource => this.validateImageResource(resource)));
        ctx.resources = validResources;
    }

    private async validateImageResource(resource: ImageResource) {
        const [valid, reason] = ImageValidator.checkImageBlob(resource.getBlob());
        if (valid) {
            return resource;
        }

        this.eventBus.emit(ImageEditEvents.ValidateError, new Error(reason));
        throw new Error(reason);
    }
}

export default ImageCreator;
